import { AbroadTag, isAbroad } from "@/components/AbroadTag";
import { WaHistory } from "@/components/client/WaHistory";
import { WaStatusPill, LastWaCharge } from "@/components/client/WaStatus";
import { ClientChatHistory } from "@/components/chat/ClientChatHistory";
import { useEffect, useState } from "react";
import { api, fmtBRL, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { X, KeyRound, CreditCard, Contact, ListVideo, ClipboardList, Smartphone, Copy, QrCode } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import { ActionToolbar } from "./ActionToolbar";
import { copyInfo, copyM3U, copySSIPTV, ssiptvUrl, copyPix, pixOk } from "@/lib/clientCopy";
import { Info, Secret, InfoCard } from "./InfoBits";
import { ActionHistory } from "./ActionHistory";
import { MacCard } from "./MacCard";
import { RenewDialog } from "./RenewDialog";
import { EditPanelDialog } from "./EditPanelDialog";
import { CourtesyDialog } from "./CourtesyDialog";
import { BouquetsDialog } from "./BouquetsDialog";
import { PlayerMacsCard } from "./PlayerMacsCard";
import { DeleteClientDialog } from "./DeleteClientDialog";

const waCharge = (c) => {
  const first = (c.name || "").split(" ")[0] || "tudo bem";
  const when = c.billing_status === "vencido" ? `venceu em ${fmtDate(c.due_date)}` : `vence em ${fmtDate(c.due_date)}`;
  const msg = `Olá ${first}! Seu acesso ${when}.${c.invoice_link ? ` Renove pelo link PIX: ${c.invoice_link}` : ""}`;
  return `https://wa.me/${(c.whatsapp || "").replace(/\D/g, "")}?text=${encodeURIComponent(msg)}`;
};

const CopyInfo = ({ c }) => (
  <button data-testid="copy-info-btn"
    onClick={() => copyInfo(c)}
    className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-xs font-bold text-sky-300 transition-colors hover:bg-sky-500/20">
    <ClipboardList className="h-4 w-4" /> Copiar informações
  </button>
);

const SendInfoApp = ({ c, onSent }) => {
  const [busy, setBusy] = useState(false);
  if (!c.app_chat) return null;
  const send = async () => {
    if (!window.confirm(`Enviar usuário, SENHA, plano e link PIX para ${c.name} pelo chat do app?\n\nConfirme que é mesmo o cliente conversando (a conversa foi aberta com o WhatsApp ${c.whatsapp}).`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${c.id}/app-info`);
      toast.success(data.push ? "Dados enviados no app + aviso no celular" : "Dados enviados no chat do app");
      onSent();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao enviar"); }
    setBusy(false);
  };
  return (
    <button data-testid="send-info-app-btn" disabled={busy} onClick={send}
      className="col-span-2 mt-1 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#D4FF3A] px-3 py-2 text-xs font-bold text-slate-950 transition-opacity hover:opacity-90 disabled:opacity-50">
      <Smartphone className="h-4 w-4" /> Enviar dados de acesso pelo app
    </button>
  );
};

const CopyM3U = ({ c }) => (
  <button data-testid="copy-m3u-btn" disabled={!c.panel_user || !c.panel_password}
    onClick={() => copyM3U(c)}
    className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs font-bold text-amber-300 transition-colors hover:bg-amber-500/20 disabled:opacity-40">
    <ListVideo className="h-4 w-4" /> Copiar M3U
  </button>
);

const CopyPix = ({ c }) => (
  <button data-testid="copy-pix-btn" disabled={!pixOk(c)} onClick={() => copyPix(c)}
    title={pixOk(c) ? c.invoice_link : "Sem link PIX válido — clique em Sincronizar"}
    className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs font-bold text-emerald-300 transition-colors hover:bg-emerald-500/20 disabled:opacity-40">
    <QrCode className="h-4 w-4" /> {pixOk(c) ? "Copiar link PIX" : "PIX indisponível"}
  </button>
);

const CopySSIPTV = ({ c }) => (
  <button data-testid="copy-ssiptv-btn" disabled={!c.panel_user || !c.panel_password} onClick={() => copySSIPTV(c)}
    title={c.panel_user && c.panel_password ? ssiptvUrl(c) : ""}
    className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-xs font-bold text-sky-300 transition-colors hover:bg-sky-500/20 disabled:opacity-40">
    <Copy className="h-4 w-4" /> Copiar URL SS IPTV
  </button>
);

const daysTo = (iso) => {
  if (!iso) return null;
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });
  return Math.round((new Date(`${iso}T00:00:00Z`) - new Date(`${today}T00:00:00Z`)) / 86400000);
};

const Dispatches = ({ items }) => (
  <div data-testid="dispatches-list" className="space-y-1.5">
    {!items.length && <div className="text-xs text-slate-500">Nenhum envio ainda.</div>}
    {items.map((l) => (
      <div key={l.id} className="flex items-center justify-between rounded-lg border border-[#1F293D] bg-[#111827] px-3 py-2 text-xs">
        <span className="text-slate-400">{{ email: "E-mail", whatsapp: "WhatsApp", app: "App" }[l.channel] || "SMS"} · {fmtDate((l.sent_at || "").slice(0, 10))}</span>
        {l.channel === "whatsapp" ? <WaStatusPill log={l} testid={`dispatch-wa-status-${l.id}`} /> : <span className={["sent", "delivered"].includes(l.status) ? "text-emerald-400" : l.status === "mock" ? "text-amber-400" : l.status === "skipped" ? "text-slate-500" : "text-red-400"}>{{ sent: "Enviado", delivered: "Entregue", mock: "Simulado", skipped: "Ignorado", failed: "Falhou" }[l.status] || l.status}</span>}
      </div>
    ))}
  </div>
);

export const ClientModal = ({ id, onClose }) => {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dlg, setDlg] = useState("");
  const [tab, setTab] = useState("acoes");
  const [rk, setRk] = useState(0);
  const reload = (cid = id) => api.get(`/clients/${cid}`).then((r) => setC(r.data));
  useEffect(() => { if (id) reload(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const k = (e) => e.key === "Escape" && !dlg && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [dlg, onClose]);
  if (!id) return null;

  const done = (nc) => { setDlg(""); setRk((x) => x + 1); reload(nc?.id || id); };
  const resync = async () => {
    setBusy(true);
    try { await api.post(`/clients/${c.id}/resync`); toast.success("Acesso ressincronizado no servidor"); done(null); }
    catch (e) { toast.error(e.response?.data?.detail || "Falha ao sincronizar"); setRk((x) => x + 1); }
    setBusy(false);
  };
  const remind = async (channel) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${c.id}/remind`, { channel });
      const r = data.results || [];
      const ok = r.filter((x) => ["sent", "mock"].includes(x.status));
      if (ok.length) toast.success(`Cobrança enviada (${ok.map((x) => x.channel).join(", ")})`);
      else toast.warning(r[0]?.error || "Envio ignorado");
      reload();
      setTab("envios");
    } catch (e) { toast.error("Erro ao enviar"); }
    setBusy(false);
  };

  const dl = c ? daysTo(c.due_date) : null;
  const initials = c ? (c.name || c.panel_user || "?").slice(0, 2).toUpperCase() : "";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6" data-testid="client-drawer">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-[#1F293D] bg-[#0F141F] shadow-2xl fade-up">
        {!c ? <div className="p-8 text-slate-400">Carregando...</div> : (
          <>
            <div className="relative flex flex-wrap items-center gap-4 border-b border-[#1F293D] px-4 py-4 pr-12 sm:px-6 sm:py-5 sm:pr-6">
              <div className="hidden h-12 w-12 items-center sm:flex justify-center rounded-xl bg-gradient-to-br from-sky-500/30 to-emerald-500/20 font-mono text-lg font-bold text-sky-200">{initials}</div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 data-testid="client-modal-name" className="truncate text-xl font-bold text-white">{c.name || c.panel_user}</h3>
                  <StatusBadge status={c.billing_status} />
                  <span className="rounded-md border border-[#202D45] bg-[#1A2234] px-2 py-0.5 font-mono text-[11px] text-sky-300">{c.account}</span>
                  {isAbroad(c.whatsapp, c.abroad) && <AbroadTag big testid="client-modal-abroad" />}
                </div>
                <div className="mt-1 truncate font-mono text-xs text-slate-400">{c.panel_user} · {c.plan}</div>
                <LastWaCharge items={c.dispatches} />
              </div>
              <div className="flex w-full items-baseline gap-2 sm:block sm:w-auto sm:text-right">
                <div className="text-[11px] uppercase tracking-wider text-slate-500">Vencimento</div>
                <div data-testid="client-modal-due" className="font-mono text-base font-bold text-white sm:text-lg">{fmtDate(c.due_date)}</div>
                {dl != null && <div className={`text-xs ${dl < 0 ? "text-red-400" : dl <= 3 ? "text-amber-400" : "text-emerald-400"}`}>{dl < 0 ? `vencido há ${-dl} dia(s)` : dl === 0 ? "vence hoje" : `faltam ${dl} dia(s)`}</div>}
              </div>
              <button data-testid="drawer-close" onClick={onClose} className="absolute right-3 top-3 rounded-lg p-1.5 sm:static text-slate-400 hover:bg-[#1A2234] hover:text-white"><X className="h-5 w-5" /></button>
            </div>

            <div className="overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
              <ActionToolbar c={c} busy={busy} onOpen={setDlg} onResync={resync} onRemind={remind}
                waHref={c.whatsapp ? waCharge(c) : undefined} courtesyOk={!c.is_trial && dl != null && dl <= 3} />

              <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-5">
                <div className="space-y-4 lg:col-span-3">
                  <InfoCard testid="card-acesso" title="Acesso" icon={KeyRound} color="#A78BFA">
                    <Info label="Usuário" value={c.panel_user} mono copy />
                    <Secret label="Senha" value={c.panel_password} />
                    <Info label="Conexões" value={c.connections} mono />
                    <Info label="Servidor" value={c.server} />
                    <CopyInfo c={c} />
                    <CopyM3U c={c} />
                    <CopyPix c={c} />
                    <CopySSIPTV c={c} />
                    <SendInfoApp c={c} onSent={() => setRk((x) => x + 1)} />
                  </InfoCard>
                  <InfoCard testid="card-assinatura" title="Assinatura" icon={CreditCard} color="#34D399">
                    <Info label="Plano" value={c.plan} />
                    <Info label="Valor" value={fmtBRL(c.value)} mono />
                    <Info label="Vencimento" value={fmtDate(c.due_date)} mono />
                    <Info label="Criado no painel" value={c.created_panel_at ? new Date(c.created_panel_at).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : fmtDate(c.created_panel)} mono />
                  </InfoCard>
                  <InfoCard testid="card-contato" title="Contato" icon={Contact} color="#38BDF8">
                    <Info label="WhatsApp" value={c.whatsapp} mono copy />
                    <Info label="E-mail" value={c.email} copy />
                    <Info label="Conta master" value={c.account} />
                    <Info label="Link PIX" value={c.invoice_link ? "disponível" : "—"} />
                  </InfoCard>
                  <MacCard c={c} onSaved={() => reload()} />
                  <PlayerMacsCard c={c} onSaved={() => { reload(); setRk((x) => x + 1); }} />
                </div>

                <div className="rounded-xl border border-[#1F293D] bg-[#0B1019] p-4 lg:col-span-2">
                  <div className="mb-3 flex gap-1 rounded-lg bg-[#111827] p-1">
                    {[["acoes", "Ações no SYSLOG"], ["envios", "Envios"], ["conversa", "WhatsApp"], ["chat", "Chat site"]].map(([k, l]) => (
                      <button key={k} data-testid={`client-tab-${k}`} onClick={() => setTab(k)}
                        className={`flex-1 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${tab === k ? "bg-sky-500 text-slate-950" : "text-slate-400 hover:text-slate-100"}`}>{l}</button>
                    ))}
                  </div>
                  <div className="max-h-[380px] overflow-y-auto pr-1">
                    {tab === "acoes" ? <ActionHistory clientId={c.id} refreshKey={rk} /> : tab === "envios" ? <Dispatches items={c.dispatches || []} /> : tab === "chat" ? <ClientChatHistory clientId={c.id} /> : <WaHistory clientId={c.id} />}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      {c && dlg === "renew" && <RenewDialog c={c} onClose={() => setDlg("")} onDone={done} />}
      {c && dlg === "edit" && <EditPanelDialog c={c} onClose={() => setDlg("")} onDone={done} />}
      {c && dlg === "courtesy" && <CourtesyDialog c={c} onClose={() => setDlg("")} onDone={done} />}
      {c && dlg === "bouquets" && <BouquetsDialog c={c} onClose={() => setDlg("")} onDone={done} />}
      {c && dlg === "delete" && <DeleteClientDialog c={c} onClose={() => setDlg("")} onDeleted={() => { setDlg(""); onClose(); }} />}
    </div>
  );
};
